/** @note inspired by https://github.com/ersagunkuruca/react-draftjs-render/blob/master/index.js */

import React, { ReactNode } from "react";

import { tryParseDraftJsContent } from "@Utils";
import { Typography } from "antd";
import { TypographyProps } from "antd/lib/typography/Typography";
import {
    DraftBlockType,
    DraftEntityType,
    DraftInlineStyleType,
    RawDraftContentBlock,
    RawDraftContentState,
    RawDraftEntity,
} from "draft-js";
import { IStringDictionary } from "../types";

const { Title } = Typography;

interface IRendererProps {
    data: string;
}

interface IRawDraftContentBlock extends RawDraftContentBlock {
    data?: { textAlign: string } | IStringDictionary;
    children: IRawDraftContentBlock[];
}

interface IRawDraftContentState extends RawDraftContentState {
    blocks: IRawDraftContentBlock[];
}

/**
 * Get the appropriate React component for a given block type
 * @param blockType - The type of the block
 */
const getBlockComponent = (blockType: DraftBlockType) => {
    switch (blockType) {
        case "unstyled":
            return "div";
        case "paragraph":
            return "p";
        case "header-one":
            return (props: TypographyProps) => <Title level={1} {...props} />;
        case "header-two":
            return (props: TypographyProps) => <Title level={2} {...props} />;
        case "header-three":
            return (props: TypographyProps) => <Title level={3} {...props} />;
        case "unordered-list-item":
            return ["li", "ul"];
        case "ordered-list-item":
            return ["li", "ol"];
        default:
            return "div";
    }
};

/**
 * Get the appropriate HTML tag for a given inline style
 * @param style - The inline style type
 */
const getStyleComponent = (style: DraftInlineStyleType) =>
    ({
        BOLD: "strong",
        ITALIC: "em",
        UNDERLINE: "u",
        STRIKETHROUGH: "s",
        CODE: "code",
    }[style] || "span");

/**
 * Get the appropriate React component for a given entity type
 * @param entityType - The type of the entity .ie image, link
 */
const getEntityComponent = (entityType: DraftEntityType) =>
    ({
        IMAGE: ({ data }: { data: IStringDictionary; children: ReactNode }) => (
            <img src={data.src} alt={data.alt} />
        ),
        LINK: ({ data, children }: { data: IStringDictionary; children: ReactNode }) => (
            <a href={data.url}>{children}</a>
        ),
    }[entityType] ?? (({ children }: { children: ReactNode }) => <>{children}</>));

/**
 * Type Predicate for IRawDraftContentState
 * @param data - The data to check
 */
const isRawDraftContentState = (data: unknown): data is IRawDraftContentState => {
    return data !== null && typeof data === "object" && "blocks" in data && "entityMap" in data;
};

/**
 * Parse a raw string into a IRawDraftContentState object
 * @param data - The raw string to parse
 */
const parseRawString = (data: string): IRawDraftContentState & { isSuccess: boolean } => {
    const emptyData = { blocks: [], entityMap: {}, isSuccess: false };

    if (!tryParseDraftJsContent(data)) {
        return emptyData;
    }

    const parsedData: unknown = JSON.parse(data);

    if (!isRawDraftContentState(parsedData)) {
        return emptyData;
    }

    return { ...parsedData, isSuccess: true };
};

/**
 * Type Predicate for CSS textAlign property
 * @param value - The value to check
 */
const isTextAlign = (value?: string): value is React.CSSProperties["textAlign"] => {
    const values = [
        "inherit",
        "initial",
        "revert",
        "revert-layer",
        "unset",
        "-webkit-match-parent",
        "center",
        "end",
        "justify",
        "left",
        "match-parent",
        "right",
        "start",
    ];

    return !!value ? values.includes(value) : false;
};

/**
 * A simple text component
 * @param children - The child elements
 */
const TextComponent: React.FC = ({ children }) => <>{children}</>;

/**
 * Converts draft js blocks into JSX with ant design components
 * @param data - The raw data to render
 */
const blocksConverter = ({ data: rawData }: IRendererProps) => {
    const data: IRawDraftContentState & { isSuccess: boolean } = parseRawString(rawData);
    const { blocks, entityMap, isSuccess } = data;

    if (!isSuccess) {
        return splitText(rawData);
    }

    /**
     * Render an array of content blocks
     * @param array - The array of content blocks to render
     */
    const renderArray = (array: IRawDraftContentBlock[]) => {
        const newArray = array.reduce<IRawDraftContentBlock[][]>(
            (acc, block) => {
                const currentGroup = acc[acc.length - 1];

                if (currentGroup.length === 0 || currentGroup[0].type === block.type) {
                    return [...acc.slice(0, -1), [...currentGroup, block]];
                } else {
                    return [...acc, [block]];
                }
            },
            [[]]
        );

        return newArray.map((group) => {
            const BlockComponent = getBlockComponent(group[0].type);
            const [InnerComponent, WrapperComponent] = Array.isArray(BlockComponent)
                ? BlockComponent
                : [BlockComponent, null];

            const inner = group.map((block): ReactNode => {
                const blockContent = block.children
                    ? [renderRichText(block), ...renderArray(block.children)]
                    : renderRichText(block);

                const style: React.CSSProperties = {};

                if (block.data && "textAlign" in block.data && isTextAlign(block.data.textAlign)) {
                    style.textAlign = block.data.textAlign;
                }

                // Handle empty blocks
                if (block.text.trim() === "" && !block.children?.length) {
                    return <div key={block.key} style={{ ...style, minHeight: "1em" }} aria-hidden="true" />;
                }

                return typeof InnerComponent === "function" ? (
                    <InnerComponent key={block.key} style={style}>
                        {blockContent}
                    </InnerComponent>
                ) : (
                    React.createElement(InnerComponent, { key: block.key, style }, blockContent)
                );
            });

            return WrapperComponent ? (
                React.createElement(WrapperComponent, { key: group[0].key }, inner)
            ) : (
                <React.Fragment key={group[0].key}>{inner}</React.Fragment>
            );
        });
    };

    /**
     * Render rich text content
     * @param block - The content block to render
     */
    const renderRichText = (block: IRawDraftContentBlock) => {
        const ranges = getRanges(block);

        return ranges.map((range, index) =>
            React.cloneElement(
                renderEntities(
                    renderStyledText(<TextComponent key={index}>{range.text}</TextComponent>, range.styles),
                    range.entities
                ),
                { key: index }
            )
        );
    };

    /**
     * Render styled text
     * @param element - The React element to style
     * @param styles - The styles to apply
     */
    const renderStyledText = (element: React.ReactElement, styles: DraftInlineStyleType[]) => {
        return styles.reduce((result, style) => {
            const styleComponent = getStyleComponent(style);
            return React.createElement(styleComponent, {}, result);
        }, element);
    };

    /**
     * Render entities
     * @param element - The React element to wrap with entities
     * @param entities - The entities to apply ie. links, images
     */
    const renderEntities = (element: React.ReactElement, entities: RawDraftEntity[]) => {
        return entities.reduce((result, entity) => {
            const EntityComponent = getEntityComponent(entity.type);
            return <EntityComponent data={entity.data}>{result}</EntityComponent>;
        }, element);
    };

    /**
     * Get the ranges for a content block
     * @param block - The content block to process
     */
    const getRanges = (block: IRawDraftContentBlock) => {
        const ranges: number[] = [0];

        block.entityRanges.forEach((range) => {
            ranges.push(range.offset);
            ranges.push(range.offset + range.length);
        });

        block.inlineStyleRanges.forEach((range) => {
            ranges.push(range.offset);
            ranges.push(range.offset + range.length);
        });

        ranges.sort((a, b) => a - b);

        const uniqueRanges = ranges.filter((v, i, a) => a.indexOf(v) === i && v !== block.text.length);

        return uniqueRanges.map((start, i) => {
            const end = uniqueRanges[i + 1] || block.text.length;
            return {
                start,
                end,
                text: block.text.slice(start, end),
                styles: block.inlineStyleRanges
                    .filter((range) => range.offset <= start && range.offset + range.length >= end)
                    .map((range) => range.style),
                entities: block.entityRanges
                    .filter((range) => range.offset <= start && range.offset + range.length >= end)
                    .map((range) => entityMap[range.key]),
            };
        });
    };

    return <div className="rendered-blocks">{renderArray(blocks)}</div>;
};

export default blocksConverter;

/**
 * Splits a given text into separate paragraphs and renders them as JSX.
 *
 * This function takes a string of text, splits it into lines based on newline characters,
 * and returns a JSX element containing each line as a separate paragraph (<p>) element.
 * It also adds line breaks (<br />) between paragraphs, except for the last one.
 *
 * @param {string} text - The input text to be split and rendered.
 *
 * @example
 * const text = "Hello\nWorld\nHow are you?";
 * const result = splitText(text);
 * Result will be equivalent to:
 * <div>
 *    <p>Hello<br /></p>
 *    <p>World<br /></p>
 *    <p>How are you?</p>
 * </div>
 */
export function splitText(text: string) {
    if (!text || typeof text !== "string") {
        return null;
    }

    const lines = text.split("\n");

    return (
        <>
            {lines.map((line, index) => (
                <p key={index}>
                    {line}
                    {index < lines.length - 1 && <br />}
                </p>
            ))}
        </>
    );
}

